import { useState, useEffect, useCallback } from 'react';
import { UserPlus, Trash2, Search, Mail, CalendarCheck2, Loader2, Users, DollarSign, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api from '@/lib/api';

const formatDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

export default function TeamManagementTab({ getAuthHeader }) {
  const [coadmins, setCoadmins] = useState([]);
  const [summary, setSummary] = useState({ total_coadmins: 0, total_events: 0, total_rsvps: 0, total_revenue_cents: 0 });
  const [loading, setLoading] = useState(false);

  // Add dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [mode, setMode] = useState('existing'); // 'existing' | 'manual'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [manualForm, setManualForm] = useState({ first_name: '', last_name: '', email: '' });
  const [canManagePromo, setCanManagePromo] = useState(false);
  const [adding, setAdding] = useState(false);

  const loadCoadmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/coadmins', { headers: getAuthHeader() });
      setCoadmins(res.data?.coadmins || []);
      setSummary(res.data?.summary || { total_coadmins: 0, total_events: 0, total_rsvps: 0, total_revenue_cents: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load co-admins');
    }
    setLoading(false);
  }, [getAuthHeader]);

  useEffect(() => { loadCoadmins(); }, [loadCoadmins]);

  // Debounced member search
  useEffect(() => {
    if (mode !== 'existing') return;
    const handle = setTimeout(async () => {
      try {
        const res = await api.get(`/admin/coadmins/members/search?q=${encodeURIComponent(searchQuery || '')}`, { headers: getAuthHeader() });
        setSearchResults(res.data?.members || []);
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchQuery, mode, getAuthHeader]);

  const resetAddDialog = () => {
    setMode('existing');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedMember(null);
    setManualForm({ first_name: '', last_name: '', email: '' });
    setCanManagePromo(false);
  };

  const handleAdd = async () => {
    setAdding(true);
    try {
      let payload;
      if (mode === 'existing') {
        if (!selectedMember) {
          toast.error('Please select a member');
          setAdding(false);
          return;
        }
        payload = { user_id: selectedMember.id, can_manage_promo_codes: canManagePromo };
      } else {
        const { first_name, last_name, email } = manualForm;
        if (!first_name || !last_name || !email) {
          toast.error('First name, last name, and email are required');
          setAdding(false);
          return;
        }
        payload = { ...manualForm, can_manage_promo_codes: canManagePromo };
      }
      const res = await api.post('/admin/coadmins', payload, { headers: getAuthHeader() });
      const wasNew = res.data?.was_new_user;
      toast.success(wasNew ? 'Co-Admin account created and welcome email sent' : 'Member promoted to Events Co-Admin');
      setAddOpen(false);
      resetAddDialog();
      await loadCoadmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add co-admin');
    }
    setAdding(false);
  };

  const handleTogglePromo = async (coadmin) => {
    const next = !coadmin.can_manage_promo_codes;
    try {
      await api.patch(`/admin/coadmins/${coadmin.id}/permissions`, { can_manage_promo_codes: next }, { headers: getAuthHeader() });
      toast.success(next ? 'Promo Codes access granted' : 'Promo Codes access revoked');
      await loadCoadmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleRemove = async (coadmin) => {
    if (!window.confirm(`Remove Events Co-Admin role from ${coadmin.first_name} ${coadmin.last_name}?\n\nTheir existing events will remain.`)) return;
    try {
      await api.delete(`/admin/coadmins/${coadmin.id}`, { headers: getAuthHeader() });
      toast.success('Co-Admin role revoked');
      await loadCoadmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove co-admin');
    }
  };

  return (
    <div data-testid="team-management-tab-content">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-navy flex items-center gap-2">
            <Users size={22} className="text-teal" /> Team Management
          </h2>
          <p className="text-text-mid text-sm mt-1">
            Add Events Co-Admins to delegate event creation and management. Co-Admins can only edit and delete the events they create.
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetAddDialog(); }}>
          <DialogTrigger asChild>
            <Button className="bg-teal hover:bg-teal-deep rounded-full" data-testid="add-coadmin-btn">
              <UserPlus size={18} className="mr-2" /> Add Events Co-Admin
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="add-coadmin-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-navy">Add Events Co-Admin</DialogTitle>
              <DialogDescription>
                Promote an existing member or create a new account. They&apos;ll receive a welcome email with login instructions.
              </DialogDescription>
            </DialogHeader>

            <Tabs value={mode} onValueChange={setMode} className="mt-2">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="existing" data-testid="coadmin-mode-existing">Existing Member</TabsTrigger>
                <TabsTrigger value="manual" data-testid="coadmin-mode-manual">Add New</TabsTrigger>
              </TabsList>

              <TabsContent value="existing" className="mt-4 space-y-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-light" />
                  <Input
                    placeholder="Search by name or email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="rounded-xl pl-9"
                    data-testid="coadmin-search-input"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto rounded-xl border border-border-subtle">
                  {searchResults.length === 0 ? (
                    <div className="p-4 text-sm text-text-mid text-center">No matching members.</div>
                  ) : (
                    searchResults.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setSelectedMember(m)}
                        className={`w-full text-left px-4 py-3 border-b border-border-subtle last:border-0 hover:bg-cream/50 transition-colors flex items-center justify-between gap-3 ${selectedMember?.id === m.id ? 'bg-teal/10' : ''}`}
                        data-testid={`coadmin-search-result-${m.id}`}
                      >
                        <div>
                          <div className="font-medium text-navy">{m.first_name} {m.last_name}</div>
                          <div className="text-xs text-text-mid">{m.email}{m.city ? ` • ${m.city}` : ''}</div>
                        </div>
                        {selectedMember?.id === m.id && (
                          <Badge className="bg-teal text-white">Selected</Badge>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="manual" className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>First Name</Label>
                    <Input
                      value={manualForm.first_name}
                      onChange={(e) => setManualForm({ ...manualForm, first_name: e.target.value })}
                      className="rounded-xl"
                      data-testid="coadmin-manual-first-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Last Name</Label>
                    <Input
                      value={manualForm.last_name}
                      onChange={(e) => setManualForm({ ...manualForm, last_name: e.target.value })}
                      className="rounded-xl"
                      data-testid="coadmin-manual-last-name"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={manualForm.email}
                    onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                    placeholder="coadmin@example.com"
                    className="rounded-xl"
                    data-testid="coadmin-manual-email"
                  />
                </div>
                <p className="text-xs text-text-mid">If this email isn&apos;t already a member, a new account will be created with a temporary password sent in the welcome email.</p>
              </TabsContent>
            </Tabs>

            {/* Permissions */}
            <div className="mt-3 rounded-xl border border-border-subtle bg-cream/30 p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={canManagePromo}
                  onChange={(e) => setCanManagePromo(e.target.checked)}
                  className="mt-1 accent-teal"
                  data-testid="coadmin-can-manage-promo"
                />
                <div>
                  <div className="font-medium text-navy text-sm">Also allow managing Promo Codes</div>
                  <p className="text-xs text-text-mid mt-0.5">Co-admin can create % off and free-trial codes (scoped to codes they create).</p>
                </div>
              </label>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
              <Button
                onClick={handleAdd}
                disabled={adding || (mode === 'existing' && !selectedMember) || (mode === 'manual' && (!manualForm.first_name || !manualForm.last_name || !manualForm.email))}
                className="bg-teal hover:bg-teal-deep rounded-full"
                data-testid="confirm-add-coadmin-btn"
              >
                {adding ? <Loader2 size={16} className="animate-spin mr-2" /> : <Mail size={16} className="mr-2" />}
                Add &amp; Send Welcome
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="coadmin-performance-summary">
        {[
          { label: 'Co-Admins', value: summary.total_coadmins, icon: Users, color: 'text-teal' },
          { label: 'Events Created', value: summary.total_events, icon: CalendarCheck2, color: 'text-blue-600' },
          { label: 'Total RSVPs', value: summary.total_rsvps.toLocaleString(), icon: BarChart3, color: 'text-amber-600' },
          { label: 'Revenue Generated', value: `$${(summary.total_revenue_cents / 100).toFixed(2)}`, icon: DollarSign, color: 'text-green-700' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl p-4 shadow-card border border-border-subtle">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-text-mid uppercase tracking-wide">{s.label}</span>
              <s.icon size={16} className={s.color} />
            </div>
            <div className="text-2xl font-bold text-navy">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Date Added</TableHead>
              <TableHead className="text-center">Events</TableHead>
              <TableHead className="text-center">Total RSVPs</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead className="text-center">Promo Access</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-text-mid">Loading…</TableCell></TableRow>
            ) : coadmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Users size={28} className="mx-auto text-text-light mb-2 opacity-40" />
                  <p className="text-text-mid">No Events Co-Admins yet.</p>
                  <p className="text-xs text-text-light mt-1">Click &ldquo;Add Events Co-Admin&rdquo; to delegate event management.</p>
                </TableCell>
              </TableRow>
            ) : (
              coadmins.map((c) => (
                <TableRow key={c.id} data-testid={`coadmin-row-${c.id}`}>
                  <TableCell className="font-medium text-navy">{c.first_name} {c.last_name}</TableCell>
                  <TableCell className="text-sm">{c.email}</TableCell>
                  <TableCell className="text-sm text-text-mid">{formatDate(c.coadmin_added_at)}</TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-seafoam/40 text-teal-deep">
                      <CalendarCheck2 size={12} className="mr-1" /> {c.events_created_count || 0}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center text-sm font-medium text-navy" data-testid={`coadmin-rsvps-${c.id}`}>
                    {(c.total_rsvps || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold text-navy" data-testid={`coadmin-revenue-${c.id}`}>
                    ${((c.total_revenue_cents || 0) / 100).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      type="button"
                      onClick={() => handleTogglePromo(c)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${c.can_manage_promo_codes ? 'bg-teal text-white hover:bg-teal-deep' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                      data-testid={`coadmin-promo-toggle-${c.id}`}
                      title={c.can_manage_promo_codes ? 'Click to revoke' : 'Click to grant'}
                    >
                      {c.can_manage_promo_codes ? 'Granted' : 'Off'}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemove(c)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                      data-testid={`remove-coadmin-${c.id}`}
                    >
                      <Trash2 size={16} className="mr-1" /> Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
